import React, { useState, useEffect } from 'react';
import { 
  FileUp, Share2, Briefcase, Maximize, FileText, DollarSign, 
  Users, BarChart3, Ruler, Download, Printer, Send, Calendar
} from 'lucide-react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend, 
  CategoryScale, LinearScale, BarElement
} from 'chart.js'; 
// import autoTable from 'jspdf-autotable';
// --- EXPORT IMPORTS ---
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

// --- FLATPICKR IMPORTS ---
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css"; 

import './Usage.css';
import Topbar from '../../component/Topbar';
import SideMenu from '../../component/SideMenu';
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';

import logo from "../../asset/img/white.png";

import autoTable from 'jspdf-autotable'; // Import the function directly

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const Usage = () => {
  const [activePeriod, setActivePeriod] = useState('This Month');
  const [activeReport, setActiveReport] = useState('Team Usage');
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);


  // --- SHARE STATE ---
  const [shareData, setShareData] = useState({
    email: '',
    format: 'PDF Summary',
    message: "Here is our team's usage report for this month. Let me know if you have any questions."
  });
  const [isSending, setIsSending] = useState(false);

  const [analytics, setAnalytics] = useState({
    totalProjects: 0,
    totalCreditsSpent: "0",
    totalLotAreaProcessed: "0",
    projectsByStatus: {},
    projectsByReportType: {}
  });

  const timePeriods = ['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'This Month', 'Last Month', 'Custom'];
  const userToken = localStorage.getItem('userToken');

  // --- API CALL ---
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const params = { timeFrame: activePeriod };
        if (activePeriod === 'Custom' && dateRange.length === 2) {
          params.startDate = dateRange[0].toISOString();
          params.endDate = dateRange[1].toISOString();
        }
        const response = await axios.get(`${baseurl}getUserProjectAnalytics`, {
          params: params,
          headers: { Authorization: `Bearer ${userToken}` }
        });
        if (response.data.success) {
          setAnalytics(response.data.analytics);
        }
      } catch (err) {
        console.error("Error fetching analytics:", err);
      }
    };

    if (userToken) {
        if (activePeriod === 'Custom') {
            if (dateRange.length === 2) fetchAnalytics();
        } else {
            fetchAnalytics();
        }
    }
  }, [userToken, activePeriod, dateRange]);

  // --- EXPORT FUNCTIONS ---

  const exportToCSV = () => {
    const data = Object.entries(analytics.projectsByReportType).map(([name, count]) => ({
      Category: name,
      Count: count,
      Percentage: `${((count / analytics.totalProjects) * 100).toFixed(1)}%`
    }));
    
    const csvContent = "data:text/csv;charset=utf-8," 
      + ["Category,Count,Percentage", ...data.map(e => `${e.Category},${e.Count},${e.Percentage}`)].join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Usage_Report_${activePeriod}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      Object.entries(analytics.projectsByReportType).map(([name, count]) => ({
        "Project Category": name,
        "Report Count": count,
        "Percentage": `${((count / analytics.totalProjects) * 100).toFixed(1)}%`
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "UsageStats");
    XLSX.writeFile(workbook, `Usage_Report_${activePeriod}.xlsx`);
  };

// ============================================================
// FRONTEND: Updated exportToPDF() — drop-in replacement
// Place this inside your Usage.jsx component
// Dependencies already in your project: jspdf, jspdf-autotable
// ============================================================



const exportToPDF = () => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PAGE_W = 210;
  const PAGE_H = 297;
  const PURPLE = [124, 58, 237];
  const PURPLE_LIGHT = [237, 233, 254];
  const DARK = [30, 41, 59];
  const GRAY = [100, 116, 139];
  const BORDER = [226, 232, 240];
  const LIGHT_BG = [248, 250, 252];
  const GREEN = [16, 185, 129];
  const PINK = [244, 63, 94];
  const ORANGE = [245, 158, 11];
  const CYAN = [6, 182, 212];

  const reportId = `RPT-${Date.now()}`;
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const areaAcres = analytics.totalLotAreaProcessed
    ? (parseFloat(analytics.totalLotAreaProcessed) / 43560).toFixed(2)
    : '0.00';

  // ── HEADER ──────────────────────────────────────────────────
  // Purple left accent bar
  doc.setFillColor(...PURPLE);
  doc.rect(0, 0, 8, 60, 'F');

  // Light header background
  doc.setFillColor(250, 251, 255);
  doc.rect(8, 0, PAGE_W - 8, 60, 'F');

  // MEASRO wordmark
  // doc.setFont('helvetica', 'bold');
  // doc.setFontSize(26);
  // doc.setTextColor(...PURPLE);
  // doc.text('MEASRO', 18, 22);


  try {
    doc.addImage(logo, 'PNG', 18, 10, 35, 12); 
  } catch (e) {
    // Fallback in case image loading fails
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...PURPLE);
    doc.text('MEASRO', 18, 22);
  }


  // Tagline
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text('Usage Report & Analytics', 18, 30);

  // Right info box (purple)
  const bx = PAGE_W - 80, by = 5, bw = 72, bh = 50;
  doc.setFillColor(...PURPLE);
  doc.roundedRect(bx, by, bw, bh, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(`Report ID: ${reportId}`, bx + 4, by + 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Usage & Analytics Report', bx + 4, by + 18);
  doc.text(`Period: ${activePeriod}`, bx + 4, by + 26);
  doc.text(`Generated on: ${today}`, bx + 4, by + 34);
  doc.text('www.measro.com', bx + 4, by + 42);

  // Header bottom border
  doc.setDrawColor(...PURPLE);
  doc.setLineWidth(0.8);
  doc.line(8, 62, PAGE_W - 8, 62);

  let cursorY = 70;

  // ── SECTION: SUMMARY STATS ───────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...PURPLE);
  doc.text('Summary Overview', 10, cursorY);
  cursorY += 2;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(10, cursorY, PAGE_W - 10, cursorY);
  cursorY += 5;

  const stats = [
    { label: 'Total Projects', value: String(analytics.totalProjects), color: PURPLE, bg: [245, 243, 255] },
    { label: 'Total Area (Acres)', value: `${areaAcres} ac`, color: GREEN, bg: [236, 253, 245] },
    { label: 'Total Reports', value: String(analytics.totalProjects), color: PINK, bg: [255, 241, 242] },
    { label: 'Credits Spent', value: String(analytics.totalCreditsSpent), color: ORANGE, bg: [255, 251, 235] },
  ];

  const cardW = (PAGE_W - 20 - 9) / 4; // 4 cards with 3 gaps of 3mm
  stats.forEach((s, i) => {
    const cx = 10 + i * (cardW + 3);
    const cy = cursorY;
    const ch = 22;

    // Card background
    doc.setFillColor(...s.bg);
    doc.roundedRect(cx, cy, cardW, ch, 2, 2, 'F');

    // Left accent bar
    doc.setFillColor(...s.color);
    doc.roundedRect(cx, cy, 2, ch, 1, 1, 'F');

    // Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...DARK);
    doc.text(s.value, cx + cardW / 2, cy + 11, { align: 'center' });

    // Label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    doc.text(s.label, cx + cardW / 2, cy + 18, { align: 'center' });
  });
  cursorY += 30;

  // ── SECTION: PROJECTS BY REPORT TYPE ─────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...PURPLE);
  doc.text('Projects by Report Type', 10, cursorY);
  cursorY += 2;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(10, cursorY, PAGE_W - 10, cursorY);
  cursorY += 4;

  const dotColors = [PURPLE, GREEN, ORANGE, PINK, CYAN];
  const typeRows = Object.entries(analytics.projectsByReportType).map(([name, count], i) => {
    const pct = analytics.totalProjects > 0
      ? `${((count / analytics.totalProjects) * 100).toFixed(1)}%`
      : '0%';
    return [name, `${count} Reports`, pct];
  });

  autoTable(doc, {
    startY: cursorY,
    head: [['Project Category', 'Count', 'Percentage']],
    body: typeRows.length > 0 ? typeRows : [['No data available', '', '']],
    theme: 'grid',
    headStyles: {
      fillColor: PURPLE,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
    },
    bodyStyles: {
      fontSize: 9,
      textColor: DARK,
      cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
    },
    alternateRowStyles: { fillColor: LIGHT_BG },
    columnStyles: {
      0: { cellWidth: 95 },
      1: { cellWidth: 55, halign: 'center' },
      2: { cellWidth: 40, halign: 'center', fontStyle: 'bold', textColor: PURPLE },
    },
    tableLineColor: BORDER,
    tableLineWidth: 0.3,
    margin: { left: 10, right: 10 },
  });
  cursorY = doc.lastAutoTable.finalY + 8;

  // ── SECTION: PROJECTS BY STATUS ───────────────────────────────
  if (Object.keys(analytics.projectsByStatus).length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...PURPLE);
    doc.text('Projects by Status', 10, cursorY);
    cursorY += 2;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(10, cursorY, PAGE_W - 10, cursorY);
    cursorY += 4;

    const totalStatus = Object.values(analytics.projectsByStatus).reduce((a, b) => a + b, 0);
    const statusRows = Object.entries(analytics.projectsByStatus).map(([status, count]) => {
      const pct = totalStatus > 0 ? `${((count / totalStatus) * 100).toFixed(1)}%` : '0%';
      return [status, String(count), pct];
    });

    autoTable(doc, {
      startY: cursorY,
      head: [['Status', 'Count', 'Share']],
      body: statusRows,
      theme: 'grid',
      headStyles: {
        fillColor: PURPLE,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      },
      bodyStyles: {
        fontSize: 9,
        textColor: DARK,
        cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
      },
      alternateRowStyles: { fillColor: LIGHT_BG },
      columnStyles: {
        0: { cellWidth: 95 },
        1: { cellWidth: 55, halign: 'center' },
        2: { cellWidth: 40, halign: 'center', fontStyle: 'bold', textColor: PURPLE },
      },
      tableLineColor: BORDER,
      tableLineWidth: 0.3,
      margin: { left: 10, right: 10 },
    });
    cursorY = doc.lastAutoTable.finalY + 8;
  }

  // ── FOOTER (all pages) ────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const fy = PAGE_H - 18;

    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.4);
    doc.line(10, fy, PAGE_W - 10, fy);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    doc.text('Generated by Measro Geo Solutions', 10, fy + 5);
    doc.text('www.measro.com', 10, fy + 10);

    const disclaimer = 'Measurements are for informational purposes only. Please verify data before use.';
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.5);
    doc.text(disclaimer, PAGE_W / 2, fy + 7, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    doc.text(`Page ${p} of ${totalPages}`, PAGE_W - 10, fy + 5, { align: 'right' });
  }

  doc.save(`Usage_Report_${activePeriod.replace(/\s+/g, '_')}.pdf`);
};

  const handlePrint = () => {
    window.print();
  };


  // --- SHARE HANDLER ---
  const handleShareReport = async (e) => {
    e.preventDefault();
    
    if (!shareData.email) {
      return Swal.fire('Error', 'Please enter a recipient email', 'error');
    }

    setIsSending(true);
    try {
      const response = await axios.post(`${baseurl}shareUsageReport`, {
        email: shareData.email,
        format: shareData.format,
        message: shareData.message,
        analyticsData: analytics, // Sending current data to backend to generate report
        timeFrame: activePeriod
      }, {
        headers: { Authorization: `Bearer ${userToken}` }
      });

      if (response.data.success) {
        Swal.fire('Success', 'Report shared successfully!', 'success');
        setShareData({ ...shareData, email: '' }); // Clear email on success
      }
    } catch (err) {
      console.error("Share error:", err);
      Swal.fire('Error', 'Failed to share report. Please try again.', 'error');
    } finally {
      setIsSending(false);
    }
  };



  // --- UI CONFIG ---
  const reportTypes = [
    { id: 'Team Usage', icon: <Users size={20} />, desc: 'Team member activity & projects' },
    { id: 'Project Summary', icon: <Briefcase size={20} />, desc: 'Projects by type and status' },
    { id: 'Financial', icon: <BarChart3 size={20} />, desc: 'Costs, credits, and expenses' },
    { id: 'Measurement', icon: <Ruler size={20} />, desc: 'Area, reports, and efficiency' },
  ];

  const doughnutData = {
    labels: Object.keys(analytics.projectsByStatus),
    datasets: [{
      data: Object.values(analytics.projectsByStatus),
      backgroundColor: ['#7c3aed', '#10b981', '#f43f5e', '#f59e0b', '#3b82f6'],
      hoverOffset: 4,
      borderWidth: 0,
    }]
  };

  const barData = {
    labels: Object.keys(analytics.projectsByReportType),
    datasets: [{
      label: 'Number of Reports',
      data: Object.values(analytics.projectsByReportType),
      backgroundColor: ['#7c3aed', '#10b981', '#f59e0b', '#ec4899', '#06b6d4'],
      borderRadius: 8,
    }]
  };

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
        <SideMenu />
        <div className="profile-content-container UD-page-wrapper p-3" style={{ flex: 1, overflow: 'auto' }}>
          
          <div className="UD-header">
            

            <div className='page-title-section '>
              <h1>Usage Report & Analytics</h1>
              <p className='page-subtitle' >Track team activity, projects, and measurements</p>
            </div>


            <div className="UD-header-actions">
              <button className="btn-save-changes" onClick={exportToPDF}><FileUp size={16} /> Export</button>
              {/* <button className="UD-btn-share"><Share2 size={16} /> Share</button> */}
            </div>
          </div>

          <div className="UD-stats-grid">
            <div className="UD-stat-card">
              <div className="UD-stat-icon icon-purple"><Briefcase size={22} /></div>
              <div className="UD-stat-info">
                <span className="UD-stat-value">{analytics.totalProjects}</span> <br/>
                <span className="UD-stat-label">Total Projects</span>
              </div>
            </div>
            <div className="UD-stat-card">
              <div className="UD-stat-icon icon-green"><Maximize size={22} /></div>
              <div className="UD-stat-info">
                <span className="UD-stat-value">
                  {(parseFloat(analytics.totalLotAreaProcessed) / 43560).toFixed(2)} ac
                </span><br/>
                <span className="UD-stat-label">Total Area (Acres)</span>
              </div>
            </div>
            <div className="UD-stat-card">
              <div className="UD-stat-icon icon-pink"><FileText size={22} /></div>
              <div className="UD-stat-info">
                <span className="UD-stat-value">{analytics.totalProjects}</span><br/>
                <span className="UD-stat-label">Total Reports</span>
              </div>
            </div>
            <div className="UD-stat-card">
              <div className="UD-stat-icon icon-orange"><DollarSign size={22} /></div>
              <div className="UD-stat-info">
                <span className="UD-stat-value">{analytics.totalCreditsSpent}</span><br/>
                <span className="UD-stat-label">Total Credits Spent</span>
              </div>
            </div>
          </div>

          <div className="UD-section-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label className="UD-section-label">Select Time Period</label>
                {activePeriod === 'Custom' && (
                    <div className="flatpickr-container" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', padding: '4px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <Calendar size={16} className="text-purple" />
                        <Flatpickr
                          options={{ mode: "range", dateFormat: "Y-m-d", defaultDate: dateRange }}
                          value={dateRange}
                          onChange={(date) => { if (date.length === 2) setDateRange(date); }}
                          className="UD-custom-flatpickr-input"
                          style={{ border: 'none', outline: 'none', fontSize: '14px', color: '#4a5568', width: '190px' }}
                          placeholder="Select Date Range"
                        />
                    </div>
                )}
            </div>
            <div className="UD-segmented-control">
              {timePeriods.map(period => (
                <button 
                  key={period} 
                  className={`UD-segment-btn ${activePeriod === period ? 'active' : ''}`} 
                  onClick={() => setActivePeriod(period)}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>

          {/* <div className="UD-section-container">
            <label className="UD-section-label">Report Type</label>
            <div className="UD-report-grid">
              {reportTypes.map(report => (
                <div key={report.id} className={`UD-report-card ${activeReport === report.id ? 'active' : ''}`} onClick={() => setActiveReport(report.id)}>
                  <div className="UD-report-icon">{report.icon}</div>
                  <h3>{report.id}</h3>
                  <p>{report.desc}</p>
                </div>
              ))}
            </div>
          </div> */}

          <div className="UD-charts-row">
            <div className="UD-chart-card">
              <h3>Projects by Status</h3>
              <div className="UD-chart-wrapper">
                {Object.keys(analytics.projectsByStatus).length > 0 ? (
                  <Doughnut data={doughnutData} options={{ cutout: '75%', plugins: { legend: { position: 'bottom' } } }} />
                ) : ( <div className="text-muted text-center py-5">No status data available</div> )}
              </div>
            </div>
            <div className="UD-chart-card">
              <h3>Report Types Used</h3>
              <div className="UD-chart-wrapper">
                {Object.keys(analytics.projectsByReportType).length > 0 ? (
                  <Bar data={barData} options={{ plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
                ) : ( <div className="text-muted text-center py-5">No report type data available</div> )}
              </div>
            </div>
          </div>

          <div className="UD-section-container">
            <div className="UD-table-header">
              <h3>Recent Project Activity</h3>
              <div className="UD-table-actions">
                <button className="UD-mini-btn" onClick={exportToExcel}><Download size={14} /> Export</button>
                <button className="UD-mini-btn">Filter</button>
              </div>
            </div>
            <div className="UD-table-responsive">
              <table className="UD-data-table">
                <thead>
                  <tr>
                    <th>Project Category</th>
                    <th>Count</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analytics.projectsByReportType).map(([name, count], i) => (
                    <tr key={i}>
                      <td>{name}</td>
                      <td>{count} Reports</td>
                      <td>{analytics.totalProjects > 0 ? ((count / analytics.totalProjects) * 100).toFixed(1) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* EXPORT OPTIONS SECTION - BUTTONS CONNECTED */}
          {/* FUNCTIONAL SHARE BOX */}
          <div className="UD-section-container">
             <div className="UD-share-box">
              <div className='UD-table-header'>
                <h3>Share This Report</h3>
              </div>
               
               <p className="UD-subtext">Share the usage report with your team or clients</p>
               <div className="UD-form-grid">
                 <div className="UD-input-group">
                   <label>Recipient Email</label>
                   <input 
                     type="email" 
                     placeholder="Enter email address" 
                     value={shareData.email}
                     onChange={(e) => setShareData({...shareData, email: e.target.value})}
                   />
                 </div>
                 <div className="UD-input-group">
                   <label>Report Format</label>
                   <select 
                     value={shareData.format}
                     onChange={(e) => setShareData({...shareData, format: e.target.value})}
                   >
                     <option>PDF Summary</option>
                     <option>Detailed CSV</option>
                   </select>
                 </div>
               </div>
               <div className="UD-input-group mt-3">
                 <label>Message (Optional)</label>
                 <textarea 
                   rows="3" 
                   value={shareData.message}
                   onChange={(e) => setShareData({...shareData, message: e.target.value})}
                 ></textarea>
               </div>
               <button 
                 className="UD-btn-send" 
                 onClick={handleShareReport}
                 disabled={isSending}
               >
                 {isSending ? 'Sending...' : <><Send size={16} /> Send Report</>}
               </button>
             </div>
             </div>
          </div>

        </div>
      </div> 
  );
};

export default Usage;